<script setup lang="ts">
import { ref } from 'vue';

import { getImageUrl } from '@apps/main/modules/api/hotels';
import {
  useBrowserStore,
  useCoreStore,
} from '@apps/main/new-desktop/core/stores';

import type { ContentApiImage } from '@libs/trip-planner-interfaces';
import {
  AppFullscreenMasonryGallery,
  AppFullscreenSingleImageGallery,
  AppIconButton,
  AppMasonryGallery,
  AppSwiperGallery,
} from '@libs/ui';

import { GalleryType } from '../models';

interface ActivityDetailsGalleryProps {
  galleryType: GalleryType;
  productText: string;
  images: ContentApiImage[];
  activityCode: string;
}
const props = defineProps<ActivityDetailsGalleryProps>();

interface ActivityDetailsGalleryEmits {
  (event: 'close'): void;
}

const emit = defineEmits<ActivityDetailsGalleryEmits>();

const isFullscreenGalleryShown = ref<boolean>(false);
const isFullScreenSingleImageGalleryShown = ref<boolean>(false);
const currentSlideIndex = ref<number>(0);

const browserStore = useBrowserStore();
const coreStore = useCoreStore();

function toggleFullscreenGallery(): void {
  isFullscreenGalleryShown.value = !isFullscreenGalleryShown.value;
}

function openFullScreenSingleImageGallery(index: number): void {
  currentSlideIndex.value = index;
  isFullScreenSingleImageGalleryShown.value = true;
}

function closeFullScreenSingleImageGallery(): void {
  isFullScreenSingleImageGalleryShown.value = false;
}

// FYI: this is temporary solution, we should use change it to proper implementation
function getImageUrlWithQuality(
  image: ContentApiImage,
  quality: 'small' | 'normal' | 'good' | 'large'
): string {
  const smallSize = 600;
  const normalSize = 800;
  const goodSize = 1200;
  const largeSize = 1600;

  if (quality === 'small') {
    return getImageUrl(image, props.activityCode, smallSize, smallSize);
  }

  if (quality === 'good') {
    return getImageUrl(image, props.activityCode, goodSize, goodSize);
  }

  if (quality === 'large') {
    return getImageUrl(image, props.activityCode, largeSize, largeSize);
  }

  return getImageUrl(image, props.activityCode, normalSize, normalSize);
}

function getImagesForMasonryGallery(): string[] {
  const gallerySize = 5;
  const firstImage = props.images[0];
  if (firstImage !== undefined && props.images.length > 0) {
    return [
      getImageUrlWithQuality(firstImage, 'normal'),
      ...props.images
        .slice(1, gallerySize)
        .map((image) => getImageUrlWithQuality(image, 'small')),
    ];
  }
  return [];
}

function getImagesForFullScreenGallery(): string[] {
  const quality = browserStore.isSmallerThan1200px ? 'small' : 'normal';
  return props.images.map((image) => getImageUrlWithQuality(image, quality));
}

function getImagesForSingleImageGallery(): string[] {
  const quality = browserStore.isSmallerThan1200px ? 'small' : 'large';
  return props.images.map((image) => getImageUrlWithQuality(image, quality));
}

function getImagesForSwipperGallery(): string[] {
  return props.images.map((image) => getImageUrlWithQuality(image, 'small'));
}
</script>

<template>
  <div class="activity-details-gallery">
    <AppSwiperGallery
      v-if="props.galleryType === GalleryType.Swiper"
      :image-urls="getImagesForSwipperGallery()"
      :display-gallery-dots="true"
      :error-image-handler="coreStore.handleImageLoadError"
      :full-gallery-aria-label="$t('a11y.open_image_gallery')"
      :left-arrow-aria-label="$t('a11y.previous_image')"
      :right-arrow-aria-label="$t('a11y.next_image')"
      @show-full-gallery="toggleFullscreenGallery()"
      @show-single-image="openFullScreenSingleImageGallery($event)"
    />
    <AppMasonryGallery
      v-if="props.galleryType === GalleryType.Masonry && props.images.length"
      :full-gallery-button-text="
        $t('planner.show_all_x_images', [props.images.length])
      "
      :image-urls="getImagesForMasonryGallery()"
      :alt-image-text="$t('a11y.image')"
      :allow-show-full-gallery-action="true"
      class="masonry-gallery"
      @show-full-gallery="toggleFullscreenGallery"
    />
    <AppFullscreenMasonryGallery
      v-if="isFullscreenGalleryShown"
      :image-urls="getImagesForFullScreenGallery()"
      :images-text="$t('planner.images')"
      :product-text="props.productText"
      :close-gallery-aria-label="$t('a11y.close_image_gallery')"
      :error-image-handler="coreStore.handleImageLoadError"
      @close="toggleFullscreenGallery"
      @image-clicked="openFullScreenSingleImageGallery($event)"
    />

    <AppFullscreenSingleImageGallery
      v-if="isFullScreenSingleImageGalleryShown"
      :image-urls="getImagesForSingleImageGallery()"
      :initial-slide="currentSlideIndex"
      :close-gallery-aria-label="$t('a11y.close_image_gallery')"
      :left-arrow-aria-label="$t('a11y.previous_image')"
      :right-arrow-aria-label="$t('a11y.next_image')"
      :error-image-handler="coreStore.handleImageLoadError"
      @close="closeFullScreenSingleImageGallery"
    />

    <div class="floating-buttons">
      <AppIconButton
        type="secondary"
        state="enabled"
        size="medium"
        :icon="{ name: 'close' }"
        :ariaLabel="$t('a11y.close_panel')"
        @click="emit('close')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.activity-details-gallery {
  border-radius: var(--border-radius-12);
  overflow: hidden;
  position: relative;

  .masonry-gallery {
    width: 100%;
    height: 425px;
  }

  .floating-buttons {
    position: absolute;
    top: var(--spacing-16);
    right: var(--spacing-16);
  }
}
</style>
